import type { PermissionAudience } from "./types";
import type { TmsCommandName } from "./commands";

/**
 * Non-bypassable command guards.
 *
 * These checks run inside `TmsCommandRegistry.execute` BEFORE business policy
 * evaluation and BEFORE any provider call, regardless of the granted permission
 * level (including `bypass_permissions`). They are the spec's "Non-Bypassable
 * Checks" (docs/tms-api-requirements.md) made real.
 *
 * This module is PURE: it receives pre-fetched facts and returns a decision.
 * The Convex layer (convex/tmsCommandHelpers.ts) is responsible for reading the
 * facts from the database and mapping them onto `GuardFacts`. Keeping the
 * decision logic pure makes every branch unit-testable without a Convex harness.
 *
 *   facts ─▶ evaluateNonBypassableGuards ─▶ { decision, blocks[] }
 *                                              │
 *            block        ───────────────────▶ command refused (hard stop)
 *            approval_required ──────────────▶ forced to approval, never auto
 *            allow        ───────────────────▶ continue to evaluatePolicy
 */

export type GuardDecision = "allow" | "approval_required" | "block";

export type GuardCode =
  | "tenant_paused_or_suspended"
  | "tenant_load_mismatch"
  | "missing_provider_credentials"
  | "disabled_provider_or_workflow"
  | "unsupported_provider_capability"
  | "emergency_lock"
  | "carrier_dnu_block"
  | "high_risk_carrier_dispatch_block"
  | "hard_authority_insurance_compliance_block"
  | "compliance_evidence_missing_or_stale"
  | "production_write_requires_approval"
  | "carrier_warning_note"
  | "low_operational_confidence"
  | "guard_facts_unavailable"
  | "invalid_approval_override"
  /** Epic B.2: load already has a different carrier — no dual book. */
  | "dual_book_lock"
  /** Epic B: commodity pack incomplete for tender/assign. */
  | "commodity_blocks_tender"
  /** Epic D: identity / double-broker mismatch. */
  | "identity_mismatch_block"
  /** Epic D: live screen-at-assign fail closed. */
  | "screen_at_assign_block"
  | "watchlist_block";

export type GuardBlock = {
  code: GuardCode;
  decision: Exclude<GuardDecision, "allow">;
  reason: string;
};

export type GuardResult = {
  decision: GuardDecision;
  blocks: GuardBlock[];
};

/** Canonical capability taxonomy (Codex #13). A provider's
 * `integrationConnections.capabilities` lists the tokens it supports; each
 * command maps to exactly one token. Coarser than per-command so providers
 * declare a small, stable set. */
export type CapabilityToken =
  | "load_write"
  | "dispatch"
  | "messaging"
  | "documents"
  | "financial";

export const CAPABILITY_BY_COMMAND: Record<TmsCommandName, CapabilityToken> = {
  create_load: "load_write",
  update_load: "load_write",
  update_load_status: "load_write",
  assign_carrier: "dispatch",
  tender_carrier: "dispatch",
  send_carrier_message: "messaging",
  send_customer_message: "messaging",
  request_tracking: "messaging",
  request_pod: "messaging",
  attach_document: "documents",
  place_payment_hold: "financial",
  release_payment_hold: "financial",
  create_invoice: "financial",
  create_settlement: "financial",
};

/** Commands that put freight on a carrier. Stale/missing compliance evidence
 * fails CLOSED for these (Tension 2). */
export const DISPATCH_LIKE_COMMANDS: ReadonlySet<TmsCommandName> = new Set<TmsCommandName>([
  "assign_carrier",
  "tender_carrier",
]);

/**
 * WS-E PROTECTED COMMANDS — these ALWAYS require approval in every
 * configuration, including `production_auto_low_risk` with a fully graduated
 * autonomy stage. Auto-tender is explicitly OFF for launch.
 *
 * The pinned launch contract names "book_carrier"; the TmsCommandName that
 * carries the `book_carrier` action is `assign_carrier`, so both are listed
 * (the literal "book_carrier" is kept defensively should a command ever adopt
 * that name). Typed as ReadonlySet<string> for that reason.
 */
export const PROTECTED_COMMANDS: ReadonlySet<string> = new Set([
  "tender_carrier",
  "book_carrier",
  "assign_carrier",
  "create_invoice",
  "create_settlement",
  "place_payment_hold",
  "release_payment_hold",
]);

/**
 * WS-E: the only commands eligible for auto-execution under
 * `production_auto_low_risk`: riskClass "low" AND canAutoExecute in
 * TMS_COMMAND_DEFINITIONS. Mirrored here (instead of importing
 * TMS_COMMAND_DEFINITIONS) to avoid a runtime circular import with
 * ./commands; command-guards.test.ts cross-checks this set against
 * TMS_COMMAND_DEFINITIONS so it cannot silently drift.
 */
export const LOW_RISK_AUTO_EXECUTABLE_COMMANDS: ReadonlySet<TmsCommandName> = new Set<TmsCommandName>([
  "send_carrier_message",
  "request_tracking",
  "request_pod",
]);

export type TenantStatus = "active" | "suspended" | "archived";
export type ManagedStatus =
  | "onboarding"
  | "sandbox"
  | "shadow_mode"
  | "production_read_and_draft"
  | "paused"
  | "suspended";
/** WS-E: tenant operating mode, read from the REAL tenantAgentConfigs row. */
export type AgentOperatingMode = "read_and_draft" | "production_auto_low_risk";
/** WS-E: per-command graduation stage from the tenantCommandGraduation row. */
export type CommandAutonomyStage =
  | "observe"
  | "draft"
  | "approval_required_write"
  | "auto_execute_low_risk"
  | "graduated";
export type IntegrationMode = "mock" | "sandbox" | "production";
export type CarrierStatus = "approved" | "review" | "blocked";
export type AuthorityStatus = "active" | "inactive" | "unknown";
export type InsuranceStatus = "verified" | "missing" | "unknown";
export type SafetyRating =
  | "satisfactory"
  | "conditional"
  | "unsatisfactory"
  | "not_rated"
  | "unknown";

export type ComplianceSnapshotFacts = {
  checkedAt: string;
  authorityStatus: AuthorityStatus;
  insuranceStatus: InsuranceStatus;
  safetyRating: SafetyRating;
  blocked: boolean;
};

export type RiskProfileFacts = {
  riskScore: number;
  recommendation: "allow" | "approval_required" | "block";
};

export type GuardFacts = {
  command: {
    commandName: TmsCommandName;
    audience: PermissionAudience;
    requiredCapability: CapabilityToken;
    isDispatchLike: boolean;
  };
  /** Resolved tenant row, or null if the tenant could not be found. */
  tenant: { status: TenantStatus } | null;
  /** Managed-agent config for the tenant, or null if not configured.
   * operatingMode is the REAL tenantAgentConfigs value (WS-E), never a
   * hardcoded literal. */
  agentConfig: {
    managedStatus: ManagedStatus;
    operatingMode: AgentOperatingMode;
    enabledWorkflows: string[];
    hasTmsCredentialRef: boolean;
  } | null;
  /** Selected provider integration for the tenant, or null if none active. */
  integration: {
    status: string;
    mode: IntegrationMode;
    capabilities: string[];
  } | null;
  /** Whether the caller supplied a carrierId for a carrier-targeted command. */
  carrierTargeted: boolean;
  /** Carrier row resolved from carrierId→carrierMc, or null if unresolved (Codex #8). */
  carrier: { status: CarrierStatus } | null;
  /** Latest raw compliance snapshot (source of truth for hard blocks, T2). */
  compliance: ComplianceSnapshotFacts | null;
  /** Latest derived risk profile (soft high-risk threshold only). */
  riskProfile: RiskProfileFacts | null;
  /** Load id resolution outcome for a load-targeted command (Codex #9). */
  loadResolution: "ok" | "mismatch" | "not_found" | "not_required";
  /** Tenant emergency lock kill switch (independent of per-action policy mode).
   * WS-E: real tenant state (permissionPolicies currentMode "emergency_lock"
   * for any policy row, or tenant paused/suspended), no longer hardcoded. */
  emergencyLock: boolean;
  /** WS-E: the tenantCommandGraduation row for THIS command; undefined when
   * absent. Absent fails CLOSED (approval) under production_auto_low_risk. */
  commandAutonomy?: { stage: CommandAutonomyStage };
  /** WS-E: tenantAgentConfigs.riskThresholds; undefined when not configured. */
  tenantRiskThresholds?: {
    autoAllowRiskScoreBelow?: number;
    approvalRequiredRiskScoreAtOrAbove?: number;
  };
  /** WS-E: MONEY-MOVING exposure ONLY (amounts from payment/settlement/invoice
   * payloads). Never load cargo value — that lives in cargoValueCents. Absent
   * fails CLOSED (approval) under production_auto_low_risk. */
  financialExposureCents?: number;
  /** WS-E: load cargo value, split out of financialExposureCents so policy can
   * still see it without conflating cargo value with money movement. */
  cargoValueCents?: number;
  /** approvalOverride must be bound to an approved approvals row + matching payload (Codex #7). */
  approvalOverride?: {
    requested: boolean;
    boundApprovalApproved: boolean;
    payloadMatches: boolean;
  };
  now: number;
  /** Compliance evidence older than this is treated as stale. */
  complianceStaleMs: number;
  /** High-risk dispatch block threshold (default 75). */
  highRiskThreshold?: number;
  /** P1.3: human carrier-trust signal derived ONLY from the noteType enum (never
   * the free-form note body). A "warning" note forces approval on carrier work. */
  carrierTrust?: { hasWarningNote: boolean };
  /** P1.3: server-derived operational confidence for the load (0-100). Below
   * `lowConfidenceThreshold` forces approval on carrier/dispatch actions. */
  operationalConfidence?: number;
  /** Low-confidence approval threshold (default 40). */
  lowConfidenceThreshold?: number;
  /**
   * Epic B.2 dual-book lock. When active on a dispatch-like command, hard block
   * so a second carrier cannot be tendered/assigned over an existing assignment.
   */
  dualBook?: { active: boolean; reason: string };
  /**
   * Epic B: commodity validation blocks tender (missing VIN, setpoint, etc.).
   * Forces block on dispatch-like commands until fields are complete.
   */
  commodityBlocksTender?: boolean;
  /**
   * Epic D: live screen-at-assign already evaluated (pure). When decision is
   * block, tender/assign hard-stops with reasons.
   */
  liveScreen?: {
    decision: "allow" | "approval_required" | "block";
    summary: string;
    blocksTender: boolean;
  };
  /** Epic D: identity check wants hard block. */
  identityBlocksTender?: boolean;
  identitySummary?: string;
};

const HIGH_RISK_DEFAULT = 75;
const LOW_CONFIDENCE_DEFAULT = 40;

/** Integration statuses that mean the provider is NOT usable. Everything else
 * (configured / healthy / degraded / active) is treated as enabled. Matches the
 * convex `integrationStatus` enum plus defensive synonyms. */
const DISABLED_INTEGRATION_STATUSES: ReadonlySet<string> = new Set([
  "not_configured",
  "down",
  "disabled",
  "error",
  "inactive",
  "unknown",
]);

/**
 * Evaluate every non-bypassable guard. Returns the strongest decision found:
 * any `block` ⇒ block; else any `approval_required` ⇒ approval_required; else allow.
 */
export function evaluateNonBypassableGuards(facts: GuardFacts): GuardResult {
  const blocks: GuardBlock[] = [];
  const block = (code: GuardCode, reason: string) =>
    blocks.push({ code, decision: "block", reason });
  const requireApproval = (code: GuardCode, reason: string) =>
    blocks.push({ code, decision: "approval_required", reason });

  // 1. Emergency lock — hardest stop.
  if (facts.emergencyLock) {
    block("emergency_lock", "tenant is emergency-locked");
  }

  // 2. Tenant status (paused/suspended/archived/missing).
  if (!facts.tenant) {
    block("tenant_paused_or_suspended", "tenant not found");
  } else if (facts.tenant.status === "archived") {
    block("tenant_paused_or_suspended", "tenant is archived");
  } else if (facts.tenant.status === "suspended") {
    block("tenant_paused_or_suspended", "tenant is suspended");
  }

  // 3. Managed-agent posture.
  if (!facts.agentConfig) {
    block("disabled_provider_or_workflow", "no managed agent config for tenant");
  } else {
    const { managedStatus } = facts.agentConfig;
    if (managedStatus === "suspended") {
      block("tenant_paused_or_suspended", "managed agent is suspended");
    } else if (managedStatus === "paused") {
      block("tenant_paused_or_suspended", "managed agent is paused");
    }
  }

  // 4. tenant/load mismatch (or unresolved load for a load-targeted command).
  if (facts.loadResolution === "mismatch") {
    block("tenant_load_mismatch", "load belongs to a different tenant");
  } else if (facts.loadResolution === "not_found") {
    block("tenant_load_mismatch", "load not found for tenant");
  }

  // 5. Workflow enabled?
  if (facts.agentConfig && !facts.agentConfig.enabledWorkflows.includes(facts.command.commandName)) {
    block("disabled_provider_or_workflow", `workflow ${facts.command.commandName} is not enabled`);
  }

  // 6. Provider integration present + active + supports the capability.
  if (!facts.integration) {
    block("disabled_provider_or_workflow", "no active provider integration");
  } else {
    if (DISABLED_INTEGRATION_STATUSES.has(facts.integration.status)) {
      block("disabled_provider_or_workflow", `provider integration is ${facts.integration.status}`);
    }
    if (!facts.integration.capabilities.includes(facts.command.requiredCapability)) {
      block(
        "unsupported_provider_capability",
        `provider does not support ${facts.command.requiredCapability}`,
      );
    }
  }

  // 7. Credentials present for real (non-mock) execution.
  const mode = facts.integration?.mode ?? "mock";
  if (mode !== "mock" && facts.agentConfig && !facts.agentConfig.hasTmsCredentialRef) {
    block("missing_provider_credentials", "no TMS credential reference configured");
  }

  // 8. Carrier DNU / block (hard). carriers.status === "blocked" is the DNU flag.
  if (facts.command.audience === "carrier" || facts.carrierTargeted) {
    if (facts.carrierTargeted && !facts.carrier) {
      // Codex #8: a carrier-targeted command whose carrier can't be resolved
      // must not silently skip the carrier gate.
      block("carrier_dnu_block", "carrier could not be resolved for compliance check");
    }
    if (facts.carrier?.status === "blocked") {
      block("carrier_dnu_block", "carrier is on do-not-use / blocked");
    }
  }

  // 9. Hard authority/insurance/safety compliance (T2: read raw snapshot).
  if (facts.carrierTargeted || facts.command.isDispatchLike) {
    evaluateCompliance(facts, block, requireApproval);
  }

  // 10. Soft high-risk dispatch block (derived profile score only).
  if (facts.command.isDispatchLike && facts.riskProfile) {
    const threshold = facts.highRiskThreshold ?? HIGH_RISK_DEFAULT;
    if (facts.riskProfile.recommendation === "block" || facts.riskProfile.riskScore >= threshold) {
      block(
        "high_risk_carrier_dispatch_block",
        `carrier risk ${facts.riskProfile.riskScore} blocks dispatch`,
      );
    }
  }

  // 10d. Dispatch always requires a load-scoped identity (no dual-book bypass).
  if (facts.command.isDispatchLike && facts.loadResolution === "not_required") {
    block("dual_book_lock", "loadId is required for tender/assign; refuse unscoped dispatch");
  }

  // 10e. Dual-book lock — cannot tender/assign a different MC when load is covered
  // or another dispatch is already in flight for this load.
  if (facts.command.isDispatchLike && facts.dualBook?.active) {
    block("dual_book_lock", facts.dualBook.reason);
  }

  // 10f. Commodity pack incomplete — fail closed on tender/assign.
  if (facts.command.isDispatchLike && facts.commodityBlocksTender) {
    block(
      "commodity_blocks_tender",
      "commodity mode required fields incomplete; cannot tender until pack is complete",
    );
  }

  // 10g. Epic D live screen-at-assign (DNU, compliance, identity, risk).
  if (facts.command.isDispatchLike && facts.liveScreen?.blocksTender) {
    block("screen_at_assign_block", facts.liveScreen.summary || "Live screen blocked tender/assign");
  } else if (facts.command.isDispatchLike && facts.liveScreen?.decision === "approval_required") {
    requireApproval(
      "screen_at_assign_block",
      facts.liveScreen.summary || "Live screen requires approval",
    );
  }

  // 10h. Identity mismatch hard block (rate con / BOL / load MC).
  if (facts.command.isDispatchLike && facts.identityBlocksTender) {
    block(
      "identity_mismatch_block",
      facts.identitySummary ?? "Carrier identity mismatch across documents",
    );
  }

  // 10b. Human carrier "warning" note (P1.3). Keyed ONLY off the noteType enum
  // (never the free-form note body). Forces approval; can only lower autonomy.
  if ((facts.command.audience === "carrier" || facts.carrierTargeted) && facts.carrierTrust?.hasWarningNote) {
    requireApproval("carrier_warning_note", "carrier has a human warning note on file; approval required");
  }

  // 10c. Low server-derived operational confidence (P1.3). Carrier/dispatch work
  // on a low-confidence load requires approval; can only lower autonomy.
  if (
    (facts.command.isDispatchLike || facts.carrierTargeted) &&
    typeof facts.operationalConfidence === "number" &&
    facts.operationalConfidence < (facts.lowConfidenceThreshold ?? LOW_CONFIDENCE_DEFAULT)
  ) {
    requireApproval(
      "low_operational_confidence",
      `operational confidence ${facts.operationalConfidence} is below threshold; approval required`,
    );
  }

  // 11. Production write posture (#11). Runs AFTER the emergency-lock guard
  // (#1), which blocks outright and always wins in summarize().
  //
  //   read_and_draft            → a real production write is never auto; forced
  //                               to approval (unchanged semantics).
  //   production_auto_low_risk  → still approval UNLESS every WS-E graduated-
  //                               autonomy condition holds (see
  //                               productionAutoLowRiskDenial). PROTECTED
  //                               COMMANDS always require approval.
  //   missing/unknown mode      → fail closed to approval.
  //
  // Sandbox / shadow execute against the sandbox adapter and are fine.
  if (mode === "production") {
    const agentConfig = facts.agentConfig;
    if (!agentConfig) {
      // Fail closed: without a config the operating mode is unknown. (Guard #3
      // already hard-blocks this case; the approval is belt-and-braces.)
      requireApproval(
        "production_write_requires_approval",
        "production write requires approval: no agent config / operating mode on file",
      );
    } else if (agentConfig.operatingMode === "production_auto_low_risk") {
      const denial = productionAutoLowRiskDenial(facts, agentConfig.managedStatus);
      if (denial) {
        requireApproval("production_write_requires_approval", denial);
      }
    } else if (agentConfig.managedStatus === "production_read_and_draft") {
      // read_and_draft — and any unrecognized mode value — fails closed to
      // approval, exactly as before WS-E.
      requireApproval(
        "production_write_requires_approval",
        "production write requires approval in read_and_draft mode",
      );
    }
  }

  // 12. approvalOverride must be a legitimately approved, payload-matching override (Codex #7).
  const override = facts.approvalOverride;
  if (override?.requested) {
    if (!override.boundApprovalApproved) {
      block("invalid_approval_override", "approvalOverride is not bound to an approved approval");
    } else if (!override.payloadMatches) {
      block("invalid_approval_override", "approvalOverride payload does not match the approved command");
    }
  }

  // A valid, payload-matching approval override satisfies approval-required guards
  // (a human already approved) but never the hard blocks (you cannot approve past a
  // suspended tenant, a DNU carrier, or failed compliance).
  const validOverride = Boolean(
    override?.requested && override.boundApprovalApproved && override.payloadMatches,
  );

  return summarize(blocks, validOverride);
}

/**
 * WS-E graduated autonomy: reason the production write must STILL go to
 * approval under `production_auto_low_risk`, or undefined when every
 * auto-execute condition holds. Every unknown fails CLOSED to approval:
 *
 *   1. managedStatus must be production_read_and_draft (the only stage the
 *      mode is reachable from; anything else is inconsistent state).
 *   2. command must NOT be a PROTECTED COMMAND (always approval — auto-tender
 *      is OFF for launch).
 *   3. command riskClass must be "low" AND canAutoExecute per
 *      TMS_COMMAND_DEFINITIONS (mirrored in LOW_RISK_AUTO_EXECUTABLE_COMMANDS).
 *   4. the tenantCommandGraduation stage for THIS command must be
 *      "auto_execute_low_risk" or "graduated"; missing row → approval.
 *   5. money-moving exposure must be exactly 0. The pinned
 *      tenantRiskThresholds shape carries only risk-score thresholds (no
 *      financial threshold), so any nonzero — or unknown — exposure requires
 *      approval.
 *   6. when tenantRiskThresholds.autoAllowRiskScoreBelow is configured, the
 *      server-derived risk score (facts.riskProfile — never caller-supplied)
 *      must be known and strictly below it.
 */
function productionAutoLowRiskDenial(
  facts: GuardFacts,
  managedStatus: ManagedStatus,
): string | undefined {
  if (managedStatus !== "production_read_and_draft") {
    return `production_auto_low_risk requires managed status production_read_and_draft (found ${managedStatus}); approval required`;
  }
  const commandName = facts.command.commandName;
  if (PROTECTED_COMMANDS.has(commandName)) {
    return `${commandName} is a protected command and always requires approval`;
  }
  if (!LOW_RISK_AUTO_EXECUTABLE_COMMANDS.has(commandName)) {
    return `${commandName} is not a low-risk auto-executable command; approval required`;
  }
  const stage = facts.commandAutonomy?.stage;
  if (stage === undefined) {
    return "no command graduation record on file; failing closed to approval";
  }
  if (stage !== "auto_execute_low_risk" && stage !== "graduated") {
    return `command autonomy stage ${stage} does not permit auto-execution; approval required`;
  }
  const exposure = facts.financialExposureCents;
  if (exposure === undefined) {
    return "financial exposure unknown; failing closed to approval";
  }
  if (exposure !== 0) {
    return `financial exposure of ${exposure} cents requires approval`;
  }
  const autoAllowBelow = facts.tenantRiskThresholds?.autoAllowRiskScoreBelow;
  if (typeof autoAllowBelow === "number") {
    const serverRiskScore = facts.riskProfile?.riskScore;
    if (typeof serverRiskScore !== "number") {
      return "no server-derived risk score available; failing closed to approval";
    }
    if (serverRiskScore >= autoAllowBelow) {
      return `server risk score ${serverRiskScore} is not below the auto-allow threshold ${autoAllowBelow}; approval required`;
    }
  }
  return undefined;
}

function evaluateCompliance(
  facts: GuardFacts,
  block: (code: GuardCode, reason: string) => void,
  requireApproval: (code: GuardCode, reason: string) => void,
) {
  const snapshot = facts.compliance;
  const stale =
    !snapshot ||
    Number.isNaN(Date.parse(snapshot.checkedAt)) ||
    facts.now - Date.parse(snapshot.checkedAt) > facts.complianceStaleMs;

  if (stale) {
    // T2: missing/stale evidence fails CLOSED on dispatch, escalates otherwise.
    if (facts.command.isDispatchLike) {
      block(
        "compliance_evidence_missing_or_stale",
        "carrier compliance evidence is missing or stale; blocking dispatch",
      );
    } else {
      requireApproval(
        "compliance_evidence_missing_or_stale",
        "carrier compliance evidence is missing or stale; approval required",
      );
    }
    return;
  }

  if (snapshot.blocked) {
    block("hard_authority_insurance_compliance_block", "carrier compliance snapshot is blocked");
  }
  if (snapshot.authorityStatus !== "active") {
    block(
      "hard_authority_insurance_compliance_block",
      `carrier authority is ${snapshot.authorityStatus}`,
    );
  }
  if (snapshot.insuranceStatus !== "verified") {
    block(
      "hard_authority_insurance_compliance_block",
      `carrier insurance is ${snapshot.insuranceStatus}`,
    );
  }
  if (snapshot.safetyRating === "unsatisfactory") {
    block("hard_authority_insurance_compliance_block", "carrier safety rating is unsatisfactory");
  }
}

function summarize(blocks: GuardBlock[], validOverride: boolean): GuardResult {
  const hardBlocks = blocks.filter((b) => b.decision === "block");
  if (hardBlocks.length > 0) {
    return { decision: "block", blocks };
  }
  if (!validOverride && blocks.some((b) => b.decision === "approval_required")) {
    return { decision: "approval_required", blocks };
  }
  return { decision: "allow", blocks: [] };
}
